import { Field } from 'formik';
import { useRef, useState } from 'react';
import { Button, Col, Form, Row, Stack } from 'react-bootstrap';
import { FormLabel, FormStepHeader } from '../../../../../components/forms';
import { Theme } from '../../../../../types/theme.type';
import { generateFileUrlSegment } from '../../../../../utils/utils';
import { StepProps } from './AddEditTheme';

type ImageMetadata = { id?: number; is_featured: boolean; destroy: boolean };

interface ThemeImageElementProps {
  onChange: (data: { file: File | null }) => void;
  onRemove: () => void;
  onDestroy: () => void;
  index: number;
  file: File | null;
  metadata: ImageMetadata | undefined;
  theme?: Theme;
}

export const ThemeImageElement = ({
  onChange,
  onRemove,
  onDestroy,
  index,
  file,
  metadata,
  theme
}: ThemeImageElementProps) => {
  const allowedTypes = ['image/jpeg', 'image/png'];
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.currentTarget.files?.[0] || null;

    if (!file) {
      onChange({ file: null });
      return;
    }

    if (!allowedTypes.includes(file.type)) {
      setError('Only JPG and PNG files are allowed.');
      event.target.value = '';
      onChange({ file: null });
      return;
    }

    setError(null);
    onChange({ file: file });
  };

  if (metadata?.destroy) return null;

  const existingImage = metadata?.id
    ? theme?.theme_images?.find((img) => img.id === metadata.id)
    : undefined;

  return (
    <div
      className="d-flex align-items-center justify-content-between px-lg-2 px-2 py-2"
      style={{
        background: '#F2F2F2',
        border: '1px solid rgba(94, 100, 105, .5)',
        borderRadius: 8
      }}
    >
      <Stack direction="vertical">
        {!metadata?.id && (
          <Form.Group>
            <FormLabel>Image</FormLabel>
            <input
              name={`images[${index}]`}
              ref={fileRef}
              className="form-control"
              accept="image/*"
              type="file"
              onChange={handleFileChange}
            />
            {file && <div className="mt-2">Selected file: {file.name}</div>}
            {error && (
              <div
                className="text-danger mt-1"
                style={{ fontSize: '0.875rem' }}
              >
                {error}
              </div>
            )}
          </Form.Group>
        )}
        {existingImage && (
          <Row>
            <Col>
              <img
                src={`${generateFileUrlSegment(existingImage.id, existingImage.s3_base_url, 'thumb')}/${existingImage.image_file_name}`}
                alt={existingImage.image_file_name}
                className="image-recipe"
              />
            </Col>
          </Row>
        )}
        <Form.Group controlId={`image_metadata[${index}].is_featured`}>
          <Form.Label>
            <Field
              type="checkbox"
              name={`image_metadata[${index}].is_featured`}
            />{' '}
            Featured
          </Form.Label>
        </Form.Group>
        <Button
          variant="primary"
          size="sm"
          className="btn-box-shadow text-uppercase border-0"
          style={{ width: 200 }}
          onClick={() => (metadata?.id ? onDestroy() : onRemove())}
        >
          Remove
        </Button>
      </Stack>
    </div>
  );
};

export const ThemeImagesForm = ({ values, setFieldValue, theme, readOnly }: StepProps) => {
  const handleChangeRegister = (data: { file: File | null }, index: number) => {
    const images = values!.images ? [...values!.images] : [];
    while (images.length <= index) {
      images.push(null);
    }
    images[index] = data.file;
    setFieldValue!('images', images);
  };

  const handleRemove = (index: number) => {
    const imageMetadata = values!.image_metadata ? [...values!.image_metadata] : [];
    imageMetadata.splice(index, 1);
    const images = values!.images ? [...values!.images] : [];
    images.splice(index, 1);
    setFieldValue!('images', images);
    setFieldValue!('image_metadata', imageMetadata);
  };

  const handleDestroy = (index: number) => {
    const imageMetadata = [...values!.image_metadata];
    imageMetadata[index] = { ...imageMetadata[index], destroy: true };
    setFieldValue!('image_metadata', imageMetadata);
  };

  const handleClick = () => {
    const images = [...(values!.images ?? [])];
    const imageMetadata = [...(values!.image_metadata ?? [])];
    // Pad so the new slot index matches image_metadata length (edit load used to leave images short).
    while (images.length < imageMetadata.length) {
      images.push(null);
    }
    images.push(null);
    imageMetadata.push({ is_featured: false, destroy: false });
    setFieldValue!('images', images);
    setFieldValue!('image_metadata', imageMetadata);
  };

  return (
    <Row>
      <Col xs={12}>
        <FormStepHeader title="Theme Images" showRequiredLegend={false} />
      </Col>
      <Col xs={12}>
        <Button
          variant="primary"
          size="sm"
          className="btn-box-shadow text-uppercase border-0"
          style={{ width: 200 }}
          onClick={() => handleClick()}
        >
          Add New Theme Image
        </Button>
        <Row>
          {values!.image_metadata.map((metadata, index) =>
            !metadata.destroy ? (
              <Col
                key={metadata.id ?? `temp-${index}`}
                xs={12}
                lg={4}
                className="mb-3"
              >
                <ThemeImageElement
                  onChange={(obj) => handleChangeRegister(obj, index)}
                  index={index}
                  onRemove={() => handleRemove(index)}
                  onDestroy={() => handleDestroy(index)}
                  file={values!.images[index]}
                  metadata={metadata}
                  theme={theme}
                />
              </Col>
            ) : null
          )}
        </Row>
      </Col>
    </Row>
  );
};
